import { FC, memo, useState } from "react";
import { Avatar, Box, Button, Stack, Typography } from "@mui/material";
import { Business } from "@mui/icons-material";
import { Organization } from "types/organization";
import { EntitySelector } from "modals/EntitySelector";
import { useParams, useNavigate, useLocation } from "react-router-dom";

interface OrganizationSelectorProps {
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSelectOrganization: (organization: Organization) => void;
  disabled?: boolean;
}

const OrganizationSelectorComponent: FC<OrganizationSelectorProps> = ({
  organizations,
  selectedOrganization,
  onSelectOrganization,
  disabled = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const handleOpenModal = () => {
    if (!disabled) {
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSelectOrganization = (organization: Organization) => {
    onSelectOrganization(organization);
    setIsModalOpen(false);

    // validate organizationId
    if (organizationId && organizationId !== organization.org_id) {
      // Verify if we are on the organization detail or edit page
      // In these cases we should not change the URL automatically
      const isOrganizationDetailPage =
        location.pathname === `/organization/${organizationId}`;
      const isOrganizationEditPage =
        location.pathname === `/organization/${organizationId}/edit`;

      if (!isOrganizationDetailPage && !isOrganizationEditPage) {
        // Replace the organizationId in the current path with the new org_id
        const newPath = location.pathname.replace(
          `/organization/${organizationId}`,
          `/organization/${organization.org_id}`,
        );
        navigate(newPath);
      }
    }
  };

  const filterCriteria = (search: string, organization: Organization) => {
    return organization.name.toLowerCase().includes(search.toLowerCase());
  };

  return (
    <Box>
      {selectedOrganization ? (
        <Stack spacing={1} direction="column" alignItems="center">
          <Avatar src={selectedOrganization?.logo_url} />
          <Box mb={2}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              textAlign="center"
            >
              {selectedOrganization.name}
            </Typography>
          </Box>
          {organizations.length > 1 && (
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={handleOpenModal}
              disabled={disabled}
            >
              Select Org
            </Button>
          )}
        </Stack>
      ) : (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<Business />}
            onClick={handleOpenModal}
            disabled={disabled}
            sx={{ py: 1.5 }}
          >
            Select Organization
          </Button>
        </Box>
      )}
      <EntitySelector
        open={isModalOpen}
        onClose={handleCloseModal}
        title="Select Organization"
        listEntities={organizations || []}
        filterCriteria={filterCriteria}
        onSelect={handleSelectOrganization}
        keyField="org_id"
        displayField="name"
        displayFieldSecondary="description"
        displayImageField="logo_url"
      />
    </Box>
  );
};

export const OrganizationSelector = memo(OrganizationSelectorComponent);
